import * as React from 'react';
import classnames from 'classnames';

import { FormField } from 'components/form';
import Input from 'components/inputs/input/Input';
import MixedSelectDropdown from 'components/inputs/mixed-select-dropdown/MixedSelectDropdown';

import { isDefined } from 'utils/helper/common';
import { DELIVERABLE_TAG_COLORS_MAP } from 'utils/deliverables';
import { __t, applyTranslations } from 'utils/language';
import { isRequired, maxLength } from 'utils/validators';

import type {
  DeliverableTag as DeliverableTagAlias,
  DeliverableTagColor,
  MixedSelectDropdownItem,
  TextEntry
} from 'types';

import './deliverable-tag.scss';

import type { SyntheticEvent } from 'react';

applyTranslations(require('./deliverable-tag.i18n.json'));

const MAX_TAG_NAME_LENGTH = 35;

type DeliverableTagAliasEnhanced = DeliverableTagAlias & {
  tag: DeliverableTagAlias | undefined | null
};

type Props = {
  deliverableTag: DeliverableTagAlias | null | undefined,
  onEdit: (tag: { tag: DeliverableTagAlias | undefined | null }) => void,
  direction?: 'column' | 'row',
  hasParent?: boolean,
  hasFixedWidthColorElement?: boolean,
  maxTagNameLength?: number,
  isModal?: boolean
};

type State = {
  deliverableTag: DeliverableTagAlias | null | undefined,
  colorItems: Array<MixedSelectDropdownItem>
};

const MSG_KEY = 'deliverable_tag';

class DeliverableTag extends React.PureComponent<Props, State> {
  static defaultProps = {
    direction: 'row',
    hasParent: true,
    isModal: false,
    hasFixedWidthColorElement: true,
    maxTagNameLength: MAX_TAG_NAME_LENGTH
  };

  state = {
    deliverableTag: this.props.deliverableTag,
    colorItems: this.getColorsOptions()
  };

  getColorsOptions(): Array<MixedSelectDropdownItem> {
    const tag = this.props.deliverableTag;

    return Object.keys(DELIVERABLE_TAG_COLORS_MAP).map(colorType => ({
      label: __t(`${MSG_KEY}.colors.${colorType}`),
      identifier: colorType,
      value: colorType,
      isDisabled: false,
      isFocused: false,
      isSelected: tag?.color === colorType,
      color: DELIVERABLE_TAG_COLORS_MAP[colorType]
    }));
  }

  handleTagColorChanged = (items: Array<MixedSelectDropdownItem>) => {
    const selectedItem = items.find(item => item.isSelected);

    // convert becuase of more specific tagColor
    this.setState(state => ({
      deliverableTag: {
        ...(state.deliverableTag || {}),
        color: selectedItem?.value as DeliverableTagColor
      },
      colorItems: items
    }), this.handleEdit);
  };

  handleTagNameChanged = (event: SyntheticEvent<TextEntry>): void => {
    const target = event.currentTarget;
    const value = target.value || '';

    this.setState(state => ({
      deliverableTag: {
        ...(state.deliverableTag || {}),
        name: value
      }
    }), this.handleEdit);
  };

  handleEdit() {
    const { isModal, onEdit } = this.props;
    const { deliverableTag } = this.state;
    const hasTag = (deliverableTag && deliverableTag.name && deliverableTag.color) || isModal;

    const deliverablePartial: DeliverableTagAliasEnhanced = {
      tag: hasTag ? deliverableTag : null
    };

    onEdit(deliverablePartial);
  }

  renderFields(): React.ReactNode {
    const { direction, isModal, hasFixedWidthColorElement, maxTagNameLength } = this.props;
    const { colorItems, deliverableTag } = this.state;
    const tag = deliverableTag || {};
    const hasTagField = tag.name || tag.color || isModal;
    const isColumnDirection = direction === 'column';

    const selectColorClasses = classnames('deliverable-tag__select-color', {
      'deliverable-tag__select-color--fixed-width': hasFixedWidthColorElement
    });

    return (
      <React.Fragment>
        <div className="deliverable-tag__tag-name">
          <FormField
            // key is necessary for recreating validation
            key={tag.color}
            label={__t(`${MSG_KEY}.label_name`)}
            tooltipMessage={__t(`${MSG_KEY}.tooltip`)}
            tooltipWidth="extra-long"
            fontSize="small"
            labelType="highlighted"
            direction={isColumnDirection ? 'row' : 'column'}
            showValidationIcon={false}
            inline
            validators={[
              (hasTagField && isRequired(tag.name)) || undefined,
              (hasTagField && maxLength(tag.name, maxTagNameLength)) || undefined
            ].filter(isDefined)}
          >
            <Input
              value={tag.name || ''}
              width={isColumnDirection ? 'fixed' : 'responsive'}
              onChange={this.handleTagNameChanged}
              charCount={{
                curCount: tag.name?.length || 0,
                maxChars: maxTagNameLength
              }}
              placeholder={__t(`${MSG_KEY}.label_name_placeholder`)}
            />
            {
              !tag.name && tag.color && (
                <div className="deliverable-tag__color-selection-error">
                  {__t(`${MSG_KEY}.errors.no_task_tag`)}
                </div>
              )
            }
          </FormField>
        </div>
        <FormField
          // key is necessary for recreating validation
          key={tag.name}
          label={__t(`${MSG_KEY}.label_color`)}
          fontSize="small"
          labelType="highlighted"
          direction={isColumnDirection ? 'row' : 'column'}
          showValidationIcon={false}
          inline
          validators={[
            (hasTagField && isRequired(tag.color)) || undefined
          ].filter(isDefined)}
        >
          <div className={selectColorClasses}>
            <MixedSelectDropdown
              name="deliverable-details-edit-tag-color"
              withColors
              withRemovable
              items={colorItems}
              placeholder={__t(`${MSG_KEY}.label_color_placeholder`)}
              onItemsRemoveAll={this.handleTagColorChanged}
              onItemsToggle={this.handleTagColorChanged}
            />
          </div>
        </FormField>
      </React.Fragment>
    );
  }

  render(): React.ReactNode {
    const { direction, hasParent } = this.props;

    if (!hasParent) {
      return this.renderFields();
    }

    const classes = classnames('deliverable-tag', {
      [`deliverable-tag--direction-${direction}`]: direction
    });

    return (
      <div className={classes}>
        {this.renderFields()}
      </div>
    );
  }
}

export default DeliverableTag;
